/**
 * FollowUpActionDetails — la parte comercial de una tarjeta de seguimiento.
 *
 * Es la referencia visual del Centro de acción: el resto de tipos usan la
 * misma gramática (`actionCardKit`) y el mismo contexto (`lib/actionContext`).
 * Lo único propio de un seguimiento es el botón de WhatsApp, porque es el
 * único tipo de acción que lleva un teléfono.
 *
 * Dos reglas que no se negocian:
 *
 *   1. Nada se inventa. Cada dato sale de `item.meta`, que el motor rellena
 *      desde el MISMO documento del lead que ya estaba leyendo.
 *
 *   2. Ninguna mutación se inventa. "Hecho" y "Reprogramar" solo aparecen si
 *      el backend las declaró en `cta_secondary`. La tarjeta virtual de
 *      "Responder a X" no las trae —taparía una acción manual que el operador
 *      no ve— y aquí sencillamente no se pintan.
 */
import React from "react";
import { ArrowRight, CheckCircle, WhatsappLogo } from "@phosphor-icons/react";
import { isValidWhatsappPhone, normalizeWhatsappPhone } from "@/lib/bulkWhatsapp";
import { contextRowsFor } from "@/lib/actionContext";
import { FOLLOWUP_SNOOZE_PRESETS, isoForSnoozePreset } from "@/lib/actionCta";
import {
  ACTION_BTN_PRIMARY, ACTION_FOOTER, ActionButtonRow, ActionLink, ContextRows,
  SnoozeButton,
} from "@/components/actionCenter/actionCardKit";

export default function FollowUpActionDetails({
  item, busy, onRunAction, now = new Date(), primaryCta = null,
}) {
  const meta = item.meta || {};
  const leadId = item.entity_id;
  const id = `ac-followup-${leadId}`;

  const keys = new Set(
    (Array.isArray(item.cta_secondary) ? item.cta_secondary : [])
      .map((c) => c.action_key),
  );
  const canComplete = keys.has("followup_complete");
  const canSnooze = keys.has("followup_snooze");
  const waReady = isValidWhatsappPhone(meta.phone);

  return (
    <div className={ACTION_FOOTER} data-testid={id}>
      <ContextRows rows={contextRowsFor(item, now)} idPrefix={id} />

      <ActionButtonRow idPrefix={id}>
        {waReady && (
          <ActionLink
            href={`https://wa.me/${normalizeWhatsappPhone(meta.phone)}`}
            testId={`${id}-whatsapp`}
          >
            <WhatsappLogo size={14} /> WhatsApp
          </ActionLink>
        )}
        <ActionLink
          to={`/leads/${encodeURIComponent(leadId)}`}
          testId={`${id}-open`}
        >
          <ArrowRight size={14} /> Abrir lead
        </ActionLink>
        {canSnooze && (
          <SnoozeButton
            presets={FOLLOWUP_SNOOZE_PRESETS}
            busy={busy}
            testId={`${id}-reschedule`}
            onPick={(key) => {
              const until = isoForSnoozePreset(key);
              if (until) onRunAction("followup_snooze", { until });
            }}
          />
        )}
        {canComplete && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onRunAction("followup_complete")}
            className={ACTION_BTN_PRIMARY}
            data-testid={`${id}-done`}
          >
            <CheckCircle size={14} weight="bold" />
            {busy ? "Guardando…" : "Hecho"}
          </button>
        )}
        {primaryCta}
      </ActionButtonRow>
    </div>
  );
}
